import {
  buildCardSignature,
  CardMetadata,
  CREDIT_CARD_ENTITY_TYPES,
  Currency,
  BlockchainId,
  BalanceTypeScaleFactorMap,
  normalizeCardValue,
  PaymentMethodEntityTypeId,
} from '@gains-com/shared';

// Re-export shared types for consumers importing from this file
export { CREDIT_CARD_ENTITY_TYPES, type CardMetadata } from '@gains-com/shared';
import { validate as bitcoinValidate } from 'bitcoin-address-validation';
import { IconKey } from '@gains-com/ui-angular';
import { PublicKey as SolanaPublicKey } from '@solana/web3.js';
import { isAddress as evmValidate } from 'web3-validator';
import {
  PaymentMethodUpdateRTM,
  PurchaseAndPaymentUpdateRTM,
  RedemptionAndPaymentUpdateRTM,
} from '@gains-com/realtime';
import { PreciseDate } from '@google-cloud/precise-date';
import { CoinflowCardMetadata } from '../components/payments/common.js';

// Re-export types from payments common
export {
  CoinflowCardMetadata,
  CoinflowMobileWalletMetadata,
} from '../components/payments/common.js';

export interface CurrencyUIConfiguration {
  ui_id: string;
  ui_name: string;
  ui_label?: string;
  ui_decimals: number;
  ui_icon: IconKey;
}

export interface NetworkUIConfiguration {
  network_ui_id: string;
  network_ui_name: string;
  network_ui_icon: IconKey;
  validateAddress: (address: string) => boolean;
}

export interface CurrencyVM
  extends Currency,
    CurrencyUIConfiguration,
    Partial<NetworkUIConfiguration> {
  blockchain_label?: string | null;
  display_name: string;
  network_name?: string | null;
  is_multi_network: boolean;
}

export interface PurchaseAndPayment extends PurchaseAndPaymentUpdateRTM {}
export interface RedemptionAndPayment extends RedemptionAndPaymentUpdateRTM {}

export const currencyUIConfigurations = Object.freeze<
  Record<string, CurrencyUIConfiguration>
>({
  BTC: {
    ui_id: 'btc',
    ui_name: 'Bitcoin',
    ui_icon: 'currency:bitcoin',
    ui_decimals: 8,
  },
  SOL: {
    ui_id: 'sol',
    ui_name: 'Solana',
    ui_icon: 'currency:solana',
    ui_decimals: 9,
  },
  ETH: {
    ui_id: 'eth',
    ui_name: 'Ethereum',
    ui_icon: 'currency:ethereum',
    ui_decimals: 18,
  },
  USDC: {
    ui_id: 'usdc',
    ui_name: 'USDC',
    ui_label: 'US Dollar Coin',
    ui_icon: 'currency:usdc',
    ui_decimals: 6,
  },
  USDT: {
    ui_id: 'usdt',
    ui_name: 'USDT',
    ui_label: 'Tether',
    ui_icon: 'currency:usdt',
    ui_decimals: 6,
  },
});

export const networkUIConfigurations = Object.freeze<
  Record<string, NetworkUIConfiguration>
>({
  [BlockchainId.Bitcoin]: {
    network_ui_id: 'bitcoin',
    network_ui_name: 'Bitcoin',
    network_ui_icon: 'currency:bitcoin',
    validateAddress: (address: string) => bitcoinValidate(address),
  },
  [BlockchainId.Solana]: {
    network_ui_id: 'solana',
    network_ui_name: 'Solana',
    network_ui_icon: 'currency:solana',
    validateAddress: (address: string) => {
      try {
        SolanaPublicKey.isOnCurve(address);
        return true;
      } catch (e) {
        return false;
      }
    },
  },
  [BlockchainId.Ethereum]: {
    network_ui_id: 'ethereum',
    network_ui_name: 'Ethereum',
    network_ui_icon: 'currency:ethereum',
    validateAddress: (address: string) => evmValidate(address),
  },
  [BlockchainId.Arbitrum]: {
    network_ui_id: 'arbitrum',
    network_ui_name: 'Arbitrum',
    network_ui_icon: 'currency:arbitrum',
    validateAddress: (address: string) => evmValidate(address),
  },
  [BlockchainId.Avalanche]: {
    network_ui_id: 'avalanche',
    network_ui_name: 'Avalanche',
    network_ui_icon: 'currency:avalanche',
    validateAddress: (address: string) => evmValidate(address),
  },
  [BlockchainId.Base]: {
    network_ui_id: 'base',
    network_ui_name: 'Base',
    network_ui_icon: 'currency:base',
    validateAddress: (address: string) => evmValidate(address),
  },
  [BlockchainId.Optimism]: {
    network_ui_id: 'optimism',
    network_ui_name: 'Optimism',
    network_ui_icon: 'currency:optimism',
    validateAddress: (address: string) => evmValidate(address),
  },
  [BlockchainId.Polygon]: {
    network_ui_id: 'polygon',
    network_ui_name: 'Polygon',
    network_ui_icon: 'currency:polygon',
    validateAddress: (address: string) => evmValidate(address),
  },
  [BlockchainId.Unichain]: {
    network_ui_id: 'unichain',
    network_ui_name: 'Unichain',
    network_ui_icon: 'currency:unichain',
    validateAddress: (address: string) => evmValidate(address),
  },
});

export const GC_SCALE_FACTOR = BalanceTypeScaleFactorMap.gold_coins;
export const SC_SCALE_FACTOR = BalanceTypeScaleFactorMap.sweeps_coins;

/**
 * Filters and deduplicates credit card payment methods for display.
 *
 * For credit card and mobile wallet payment methods (Apple Pay, Google Pay), deduplication is based on
 * card metadata (cardType, last4) instead of entity IDs. This handles the case where mobile wallets
 * create multiple tokenizations for the same underlying card.
 *
 * Returns unique payment methods sorted by created_at descending, keeping the most recently created
 * payment method for each unique card signature.
 *
 * @param paymentMethods - Array of payment method updates to filter and deduplicate
 * @param targetEntityTypeIds - Optional array of entity type IDs to filter to. If not provided or empty,
 *                              filters to CREDIT_CARD_ENTITY_TYPES by default.
 * @returns Filtered and deduplicated array of payment methods, or null if input is null/empty
 */
export function getCreditCardPaymentMethodsForDisplay(
  paymentMethods: PaymentMethodUpdateRTM[] | null,
  targetEntityTypeIds?: string[]
): PaymentMethodUpdateRTM<CoinflowCardMetadata>[] | null {
  // Determine which entity types to filter to
  const filterEntityTypes = targetEntityTypeIds?.length
    ? targetEntityTypeIds
    : (CREDIT_CARD_ENTITY_TYPES as string[]);

  // Check if we're filtering to any credit card types
  const hasCreditCardTarget = filterEntityTypes.some((id) =>
    (CREDIT_CARD_ENTITY_TYPES as string[]).includes(id)
  );

  if (!hasCreditCardTarget) return null;

  if (!paymentMethods) return null;

  return Object.values(
    (
      paymentMethods.filter(
        (pm) =>
          (CREDIT_CARD_ENTITY_TYPES as string[]).includes(
            pm.payment_method_entity_type_id
          ) && !pm.deleted
      ) as PaymentMethodUpdateRTM<CoinflowCardMetadata>[]
    )
      .sort(
        (a, b) =>
          new PreciseDate(b.created_at).getTime() -
          new PreciseDate(a.created_at).getTime()
      )
      .reduce((acc, pm) => {
        // Dedupe by card metadata (cardType + last4) via the shared
        // buildCardSignature helper so the in-memory key format is
        // consistent with the backend collectCardSignatures util.
        // Handles Apple Pay/Google Pay tokenizations of the same
        // physical card collapsing into one row.
        const meta = pm.metadata as CardMetadata | null;

        let key: string;
        if (meta?.cardType && meta?.last4) {
          key = `${pm.account_id}:${buildCardSignature(
            normalizeCardValue(meta.cardType),
            normalizeCardValue(meta.last4)
          )}`;
        } else {
          // Fallback: dedupe by entity IDs
          key = `${pm.account_id}:${pm.payment_method_entity_type_id}:${pm.payment_method_entity_id}`;
        }

        acc[key] = acc[key] ?? pm;
        return acc;
      }, {} as Record<string, PaymentMethodUpdateRTM<CoinflowCardMetadata>>)
  );
}
